import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Tooltip,
  FormHelperText,
  Autocomplete,
  Alert,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
  Divider,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import NotificationsIcon from "@mui/icons-material/Notifications";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { format } from "date-fns";

import { RootState } from "../../store/Store";
import {
  fetchData,
  postData,
  FetchDataSuccessAction,
  FetchDataFailureAction,
} from "../../store/actions";
import { useCustomToast } from "../../components/Methods/SnackBar";
import PageTitle from "../../components/Page Component/PageTitle";
import { setIsLoading } from "../../store/LoadingSlice";
import {
  processComplianceAssignmentsUrl,
  processComplianceTemplatesUrl,
  projectsUrl,
  assignableComplianceUsersUrl,
  bulkAssignComplianceUrl,
  projectSprintsUrl,
  previousManagerUrl,
  sendComplianceNotificationsUrl,
} from "../../Service/Apis";
import { formatDateConsistent } from "../../utils/dateUtils";
import { titleCase, getComplianceStatusColor } from "./utils";
import { getDataGridPaginationProps } from "../../utils/paginationConfig";
import { isPCManager } from "../../utils/compliancePermissions";
import CustomNoRowsOverlay from "../../components/Data/NoRowsOverlayComponent";
import axios from "axios";

interface Template {
  template_id: string;
  template_name: string;
  template_data: {
    questions: { id: string; question: string }[];
    sprintCount: number;
    sprintNames?: { id: string; name: string }[];
  };
  is_active: boolean;
}

interface ProjectManager {
  id: string;
  name: string;
  email: string;
}

interface Project {
  project_id: string;
  project_name: string;
  project_key: string;
  team_id: string;
  team_name: string;
  project_manager: string | null;
  manager_name: string | null;
}

interface Sprint {
  sprint_id: string;
  sprint_name: string;
  start_date: string;
  end_date: string;
  sprint_state: string;
}

interface ComplianceAssignment {
  assignment_id: string;
  project: string;
  project_name: string;
  template: string;
  template_name: string;
  assigned_to: string;
  assigned_to_name: string;
  assigned_by: string;
  assigned_by_id?: string;
  assigned_by_name: string;
  due_date: string;
  due_date_str: string;
  completion_date: string | null;
  status: "pending" | "in_progress" | "completed" | "overdue";
  month_year: string;
  month_year_str: string;
  compliance_score?: {
    overall_score: number;
    category_scores: Record<string, {
      name: string;
      percentage_weight: number;
      yes_count: number;
      no_count: number;
      total_answered: number;
      score: number;
    }>;
    total_yes: number;
    total_no: number;
    total_answered: number;
  };
}

const ProcessComplianceAssignments: React.FC = () => {
  const [assignments, setAssignments] = useState<ComplianceAssignment[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [complianceOfficers, setComplianceOfficers] = useState<ProjectManager[]>([]); // For project-based assignee dropdown
  const [users, setUsers] = useState<ProjectManager[]>([]); // For direct assignment dropdown (all employees)
  const [customDueDate, setCustomDueDate] = useState<Date | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<Date | null>(new Date());
  
  // Assignment type selection
  const [assignmentType, setAssignmentType] = useState<'project_based' | 'direct'>('project_based');
  
  // Project-based assignment state
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [selectedOfficer, setSelectedOfficer] = useState<ProjectManager | null>(null); // PCO single-select
  const [previousManager, setPreviousManager] = useState<ProjectManager | null>(null);
  const [projectSprints, setProjectSprints] = useState<Sprint[]>([]);
  const [selectedSprints, setSelectedSprints] = useState<Sprint | null>(null); // Changed to single-select
  
  // Direct assignment state
  const [selectedUsers, setSelectedUsers] = useState<ProjectManager | null>(null); // Changed to single-select
  const [cycleConfig, setCycleConfig] = useState({
    cycleCount: 3,
    cycleNames: [] as { name: string; original_id: string | null }[]
  });
  
  // Loading states
  const [loadingOfficers, setLoadingOfficers] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userLoadRetries, setUserLoadRetries] = useState(0);
  
  const [formData, setFormData] = useState({
    template_id: "",
    project_ids: [] as string[], // Keept for backward compatibility
    month_year: format(new Date(), "yyyy-MM"),
    manager_overrides: {} as Record<string, string>,
    default_manager_id: "",
    custom_due_date: "",
  });

  // Count projects without managers
  const [projectsWithoutManager, setProjectsWithoutManager] = useState<
    Project[]
  >([]);

  const today = useMemo(() => new Date(), []);

  const showToast = useCustomToast();
  const dispatch = useDispatch();
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const selectedRole = useSelector(
    (state: RootState) => state.auth.selectedRole
  );
  const personId = useSelector(
    (state: RootState) => state.auth.userId
  );
  const isLoading = useSelector((state: RootState) => state.loading.isLoading);
  const navigate = useNavigate();

  // Access control - redirect if not HR, Admin, or Process Compliance Manager
  useEffect(() => {
    if (selectedRole && selectedRole !== "HR" && selectedRole !== "Admin" && !isPCManager(selectedRole)) {
      showToast("Access denied. This page is only accessible to HR, Admin, and Process Compliance Manager.", "error", true);
      navigate("/");
    }
  }, [selectedRole]);

  useEffect(() => {
    if (selectedRole === "HR" || selectedRole === "Admin" || isPCManager(selectedRole)) {
      fetchAssignments();
      fetchTemplates();
      fetchProjects();
      fetchComplianceOfficers();
    }
  }, [selectedRole]);

  useEffect(() => {
    // For project-based: project is valid as long as a PCO is selected 
    if (assignmentType === 'project_based' && selectedProject && !selectedOfficer) {
      setProjectsWithoutManager([selectedProject]);
    } else {
      setProjectsWithoutManager([]);
    }
  }, [assignmentType, selectedProject, selectedOfficer]);

  // Fetch users when direct assignment is selected
  useEffect(() => {
    if (assignmentType === 'direct' && openDialog) {
      fetchUsers();
    }
  }, [assignmentType, openDialog]);

  // Reset form when assignment type changes
  useEffect(() => {
    if (openDialog) {
      resetFormForAssignmentType();
    }
  }, [assignmentType]);

  const fetchAssignments = async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await dispatch2(
        fetchData(`${processComplianceAssignmentsUrl}?role=${selectedRole}&lightweight=true`)
      );
      setAssignments(response as ComplianceAssignment[]);
    } catch (error) {
      console.error("Error fetching assignments:", error);
      showToast("Error fetching assignments", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await dispatch2(
        fetchData(`${processComplianceTemplatesUrl}?role=${selectedRole}&lightweight=true`)
      );
      // Filter only active templates
      setTemplates(
        (response as Template[]).filter((template) => template.is_active)
      );
    } catch (error) {
      console.error("Error fetching templates:", error);
      showToast("Error fetching templates", "error", true);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await dispatch2(
        fetchData(`${projectsUrl}?role=${selectedRole}`)
      );
      setProjects(response as Project[]);
    } catch (error) {
      console.error("Error fetching projects:", error);
      showToast("Error fetching projects", "error", true);
    }
  };

  const fetchComplianceOfficers = async () => {
    setLoadingOfficers(true);
    try {
      // Fetch users with 'Process Compliance Officer' role for project-based assignment
      const response = await dispatch2(
        fetchData(
          `${assignableComplianceUsersUrl}?role=${selectedRole}&role_filter=Process Compliance Officer`
        )
      );
      setComplianceOfficers(response as ProjectManager[]);
    } catch (error) {
      console.error("Error fetching compliance officers:", error);
      showToast("Error fetching compliance officers", "error", true);
    } finally {
      setLoadingOfficers(false);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      // For direct assignments, fetch all active employees
      const response = await dispatch2(
        fetchData(`${assignableComplianceUsersUrl}?role=${selectedRole}`)
      );
      setUsers(response as ProjectManager[]);
      setUserLoadRetries(0);
    } catch (error) {
      console.error("Error fetching users:", error);

      if (userLoadRetries < 5) {
        setUserLoadRetries(prev => prev + 1);
        setTimeout(() => fetchUsers(), 1000);
      } else {
        showToast("Failed to load employees after multiple attempts", "error", true);
        setUserLoadRetries(0);
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchProjectSprints = async (projectId: string) => {
    try {
      const response = await dispatch2(
        fetchData(`${projectSprintsUrl}?project_id=${projectId}&role=${selectedRole}`)
      );
      const sprints = response.sprints || [];

      // Sort by end_date descending (latest first)
      const sorted: Sprint[] = sprints.sort((a: Sprint, b: Sprint) =>
        new Date(b.end_date || '1900-01-01').getTime() - new Date(a.end_date || '1900-01-01').getTime()
      );

      setProjectSprints(sorted);
      // Auto-select the latest/first active sprint as default (single-select)
      setSelectedSprints(sorted.length > 0 ? sorted[0] : null);
    } catch (error) {
      console.error("Error fetching project sprints:", error);
      setProjectSprints([]);
      setSelectedSprints(null);
    }
  };

  const fetchPreviousManager = async (projectId: string) => {
    try {
      const response = await dispatch2(
        fetchData(`${previousManagerUrl}?project_id=${projectId}&role=${selectedRole}`)
      );
      if (response.manager) {
        setPreviousManager(response.manager);
        // Pre-select previous officer in the PCO dropdown if they appear in the officers list
        setSelectedOfficer((prev) => {
          if (prev) return prev; // don't overwrite a deliberate selection
          const found = complianceOfficers.find((o) => o.id === response.manager.id);
          return found || null;
        });
      } else {
        setPreviousManager(null);
      }
    } catch (error) {
      console.error("Error fetching previous manager:", error);
      setPreviousManager(null);
    }
  };

  const resetFormForAssignmentType = () => {
    if (assignmentType === 'project_based') {
      setSelectedUsers(null);
      setCycleConfig({
        cycleCount: 3,
        cycleNames: Array.from({ length: 3 }, (_, i) => ({
          name: `Cycle ${i + 1}`,
          original_id: null
        }))
      });
    } else {
      setSelectedProject(null);
      setSelectedOfficer(null);
      setProjectSprints([]);
      setSelectedSprints(null);
      setPreviousManager(null);
      setFormData(prev => ({
        ...prev,
        default_manager_id: "",
        manager_overrides: {}
      }));
    }
  };

  const handleSendNotifications = async () => {
    try {
      dispatch(setIsLoading(true));
      const response = (await dispatch2(
        postData(`${sendComplianceNotificationsUrl}?role=${selectedRole}`, {})
      )) as any;

      if (response && response.notifications_sent > 0) {
        showToast(
          response.message || `Sent ${response.notifications_sent} notifications successfully`,
          "success",
          true
        );
      } else {
        showToast("No pending notifications to send", "info", true);
      }
    } catch (error) {
      console.error("Error sending notifications:", error);
      showToast("Error sending notifications", "error", true);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const handleOpenDialog = () => {
    // Reset all form state
    setAssignmentType('project_based');
    setSelectedProject(null);
    setSelectedOfficer(null);
    setSelectedUsers(null);
    setPreviousManager(null);
    setProjectSprints([]);
    setSelectedSprints(null);
    setCustomDueDate(null);

    // Reset cycle config
    setCycleConfig({
      cycleCount: 3,
      cycleNames: Array.from({ length: 3 }, (_, i) => ({
        name: `Cycle ${i + 1}`,
        original_id: null
      }))
    });
    
    setFormData({
      template_id: "",
      project_ids: [],
      month_year: format(new Date(), "yyyy-MM"),
      manager_overrides: {},
      default_manager_id: "",
      custom_due_date: "",
    });
    
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleAssignmentTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAssignmentType(event.target.value as 'project_based' | 'direct');
  };

  const handleProjectChange = (project: Project | null) => {
    setSelectedProject(project);
    setSelectedOfficer(null);
    if (project) {
      fetchProjectSprints(project.project_id);
      fetchPreviousManager(project.project_id);
    } else {
      setProjectSprints([]);
      setSelectedSprints(null);
      setPreviousManager(null);
    }
  };

  const handleTemplateChange = (e: any) => {
    setFormData({
      ...formData,
      template_id: e.target.value,
    });
  };

  const handleCycleCountChange = (count: number) => {
    const validCount = Math.max(1, count); // Minimum 1 cycle
    
    setCycleConfig(prev => {
      const newCycleNames = [...prev.cycleNames];
      
      if (validCount > prev.cycleNames.length) {
        for (let i = prev.cycleNames.length; i < validCount; i++) {
          newCycleNames.push({
            name: `Cycle ${i + 1}`,
            original_id: null
          });
        }
      } else if (validCount < prev.cycleNames.length) {
        newCycleNames.splice(validCount);
      }
      
      return {
        ...prev,
        cycleCount: validCount,
        cycleNames: newCycleNames
      };
    });
  };

  const handleCycleNameChange = (index: number, name: string) => {
    setCycleConfig(prev => {
      const newCycleNames = [...prev.cycleNames];
      newCycleNames[index] = {
        ...newCycleNames[index],
        name: name
      };
      return {
        ...prev,
        cycleNames: newCycleNames
      };
    });
  };

  const handleDueDateChange = (date: Date | null) => {
    if (date) {
      setCustomDueDate(date);
      setFormData({
        ...formData,
        custom_due_date: format(date, "yyyy-MM-dd"),
      });
    } else {
      setCustomDueDate(null);
      setFormData({
        ...formData,
        custom_due_date: "",
      });
    }
  };

  const handleMonthChange = (date: Date | null) => {
    if (date) {
      setSelectedMonth(date);
      setFormData({
        ...formData,
        month_year: format(date, "yyyy-MM"),
      });
    }
  };

  const handleManagerOverride = (projectId: string, managerId: string) => {
    setFormData({
      ...formData,
      manager_overrides: {
        ...formData.manager_overrides,
        [projectId]: managerId,
      },
    });
  };

  const handleDefaultManagerChange = (e: any) => {
    setFormData({
      ...formData,
      default_manager_id: e.target.value,
    });
  };

  const handleSubmit = async () => {
    try {
      dispatch(setIsLoading(true));

      // Basic validation
      if (!formData.template_id) {
        showToast("Please select a template", "error", true);
        dispatch(setIsLoading(false));
        return;
      }

      // Assignment type specific validation
      if (assignmentType === 'project_based') {
        if (!selectedProject) {
          showToast("Please select a project", "error", true);
          dispatch(setIsLoading(false));
          return;
        }

        // Validate sprint selection (must select exactly one sprint)
        if (!selectedSprints) {
          showToast("Please select a sprint", "error", true);
          dispatch(setIsLoading(false));
          return;
        }

        // PCO is mandatory for project-based assignments
        if (!selectedOfficer) {
          showToast("Please select a Process Compliance Officer to assign this compliance to", "error", true);
          dispatch(setIsLoading(false));
          return;
        }
      } else {
        // Direct assignment validation (must select exactly one employee)
        if (!selectedUsers) {
          showToast("Please select one employee for direct assignment", "error", true);
          dispatch(setIsLoading(false));
          return;
        }

        // Validate cycle configuration
        const emptyCycleNames = cycleConfig.cycleNames.some(c => !c.name.trim());
        if (emptyCycleNames) {
          showToast("All cycle names must be filled", "error", true);
          dispatch(setIsLoading(false));
          return;
        }

        // Validate cycle count matches configured cycles
        if (cycleConfig.cycleCount !== cycleConfig.cycleNames.length) {
          showToast("Cycle count must match the number of configured cycles", "error", true);
          dispatch(setIsLoading(false));
          return;
        }
      }

      // Due date is always required
      if (!formData.custom_due_date) {
        showToast("Please select a due date", "error", true);
        dispatch(setIsLoading(false));
        return;
      }

      // Prepare submission data
      const submitData = {
        template_id: formData.template_id,
        assignment_type: assignmentType,
        month_year: formData.month_year,
        custom_due_date: formData.custom_due_date,
        
        ...(assignmentType === 'project_based' ? {
          project_ids: [selectedProject!.project_id],
          // Always override with the selected PCO — ignore project.project_manager
          manager_overrides: { [selectedProject!.project_id]: selectedOfficer!.id },
          default_manager_id: selectedOfficer!.id,
          sprint_config: {
            sprintCount: 1,
            sprints: selectedSprints ? [{
              name: selectedSprints.sprint_name,
              original_id: selectedSprints.sprint_id,
            }] : []
          }
        } : {
          // For direct assignments, we still need to provide project_ids as empty array to satisfy backend validation
          project_ids: [],
          assigned_users: selectedUsers ? [selectedUsers.id] : [],
          cycle_config: {
            cycleCount: cycleConfig.cycleCount,
            cycles: cycleConfig.cycleNames
          }
        })
      };

      const response = await axios.post(
        `${bulkAssignComplianceUrl}?role=${selectedRole}`,
        submitData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response && response.data) {
        if (response.data.success) {
          const created: string[] = response.data.assignments_created || [];
          // Support both field names for backward compatibility
          const skippedItems: string[] = response.data.skipped_items || response.data.skipped_projects || [];

          // Only show success toast when at least one assignment was actually created
          if (created.length > 0) {
            showToast(
              response.data.message || `${created.length} assignment${created.length > 1 ? "s" : ""} created successfully`,
              "success",
              true
            );
          }

          if (skippedItems.length > 0) {
            console.log("Skipped assignments details:", skippedItems);

            // Strip the trailing "(reason)" parenthetical so only the project/user name is shown
            const skippedNames = skippedItems.map((skip: string) =>
              typeof skip === "string"
                ? skip.replace(/\s*\([^)]*\)\s*$/, "").trim()
                : String(skip)
            );

            showToast(
              `Some assignments were skipped: ${skippedNames.join(", ")}.`,
              created.length > 0 ? "warning" : "error",
              true
            );
          }

          if (created.length === 0 && skippedItems.length === 0) {
            showToast(response.data.message || "No assignments were created", "info", true);
          }

          // Close dialog and refresh only when something was created
          if (created.length > 0) {
            handleCloseDialog();
            fetchAssignments();
          }
        } else {
          showToast(
            response.data.message || "Error creating assignments",
            "error",
            true
          );
        }
      }
    } catch (error: any) {
      console.error("Error creating assignments:", error);
      
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const errorMessage = error.response.data?.error || 
                           error.response.data?.message || 
                           `Server error: ${error.response.status}`;
        showToast(errorMessage, "error", true);
      } else if (error.request) {
        // Request was made but no response received
        showToast("Network error: Unable to reach server", "error", true);
      } else {
        // Something else happened
        showToast("Error creating assignments", "error", true);
      }
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  // --- Filter state ---
  const COMPLIANCE_STATUSES = ["pending", "in_progress", "completed", "overdue"] as const;

  const [columnFilters, setColumnFilters] = useState({
    project_name: "",
    template_name: "",
    assigned_to_name: "",
    status: "",
  });

  const handleFilterChange = (field: keyof typeof columnFilters, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setColumnFilters({ project_name: "", template_name: "", assigned_to_name: "", status: "" });
  };

  const hasActiveFilters = Object.values(columnFilters).some((v) => v !== "");

  const filteredAssignments = useMemo(() => {
    const { project_name, template_name, assigned_to_name, status } = columnFilters;

    return assignments.filter((row) => {
      if (project_name && !(row.project_name || '').toLowerCase().includes(project_name.toLowerCase())) return false;
      if (template_name && !(row.template_name || '').toLowerCase().includes(template_name.toLowerCase())) return false;
      if (assigned_to_name && !(row.assigned_to_name || '').toLowerCase().includes(assigned_to_name.toLowerCase())) return false;
      if (status && row.status !== status) return false;
      return true;
    });
  }, [assignments, columnFilters]);

  const exportToCSV = () => {
    if (filteredAssignments.length === 0) return;

    const rows = filteredAssignments.map((row, idx) => ({
      "Sr No.": idx + 1,
      "Project": row.project_name,
      "Template": row.template_name,
      "Period": row.month_year_str,
      "Assigned To": row.assigned_to_name,
      "Due Date": formatDateConsistent(row.due_date),
      "Status": titleCase(row.status),
      "Score":
        row.compliance_score && row.compliance_score.total_answered > 0
          ? `${row.compliance_score.total_yes}/${row.compliance_score.total_answered} (${row.compliance_score.overall_score.toFixed(1)}%)`
          : "N/A",
    }));

    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((r) =>
        headers.map((h) => `"${String((r as any)[h]).replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `compliance-assignments-${format(new Date(), "dd-MM-yyyy")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Dropdown option lists for filter controls
  const projectOptions = useMemo(
    () => projects.map((p) => p.project_name).sort(),
    [projects]
  );
  const templateOptions = useMemo(
    () => templates.map((t) => t.template_name).sort(),
    [templates]
  );
  const assigneeOptions = useMemo(
    () =>
      [...new Set(assignments.map((a) => a.assigned_to_name))]
        .filter(Boolean)
        .sort(),
    [assignments]
  );

  // DataGrid column definitions
  const complianceColumns: GridColDef[] = [
    {
      field: "sr_no",
      headerName: "Sr No.",
      width: 80,
      sortable: false,
      valueGetter: (params) =>
        filteredAssignments.findIndex(
          (a) => a.assignment_id === params.row.assignment_id
        ) + 1,
      renderHeader: () => <strong>Sr No.</strong>,
    },
    {
      field: "project_name",
      headerName: "Project",
      flex: 1,
      minWidth: 300,
      renderHeader: () => <strong>Project</strong>,
      renderCell: (params) => params.value || "—",
    },
    {
      field: "template_name",
      headerName: "Template",
      width: 200,
      renderHeader: () => <strong>Template</strong>,
    },
    {
      field: "month_year_str",
      headerName: "Period",
      width: 120,
      renderHeader: () => <strong>Period</strong>,
    },
    {
      field: "assigned_to_name",
      headerName: "Assigned To",
      width: 180,
      renderHeader: () => <strong>Assigned To</strong>,
    },
    {
      field: "due_date",
      headerName: "Due Date",
      width: 130,
      valueFormatter: (params) => formatDateConsistent(params.value),
      renderHeader: () => <strong>Due Date</strong>,
    },
    {
      field: "status",
      headerName: "Status",
      width: 140,
      renderHeader: () => <strong>Status</strong>,
      renderCell: (params) => (
        <Chip
          label={titleCase(params.value)}
          size="small"
          sx={{
            backgroundColor: getComplianceStatusColor(params.value),
            color: "white",
          }}
        />
      ),
    },
    {
      field: "compliance_score",
      headerName: "Score",
      width: 200,
      sortable: false,
      renderHeader: () => <strong>Score</strong>,
      renderCell: (params) => {
        const cs = params.row.compliance_score;
        if (!cs || cs.total_answered === 0) {
          return (
            <Typography variant="body2" color="textSecondary">
              N/A
            </Typography>
          );
        }
        return (
          <Tooltip
            title={`${cs.total_yes} Yes out of ${cs.total_answered} answered (N/A excluded)`}
          >
            <Chip
              label={`${cs.total_yes}/${cs.total_answered} (${cs.overall_score.toFixed(1)}%)`}
              size="small"
              color={
                cs.overall_score >= 80
                  ? "success"
                  : cs.overall_score >= 60
                  ? "warning"
                  : "error"
              }
            />
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderHeader: () => <strong>Actions</strong>,
      renderCell: (params) => (
        <Tooltip
          title={params.row.status === "completed" ? "View Form" : "Edit Form"}
        >
          <Link to={`/process-compliance-form/${params.row.assignment_id}`}>
            <IconButton color="primary" size="small">
              {params.row.status === "completed" ? (
                <VisibilityIcon />
              ) : (
                <EditIcon />
              )}
            </IconButton>
          </Link>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <Container maxWidth={false} sx={{ mt: 12, mb: 10, px: { xs: 1, sm: 2, md: 3 } }}>
        <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>

          {/* ── Row 1: Page heading + action buttons ── */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            flexWrap="wrap"
            gap={1}
          >
            <PageTitle>Process Compliance Assignments</PageTitle>
            {(selectedRole === "HR" || selectedRole === "Admin" || isPCManager(selectedRole)) && (
              <Box display="flex" gap={2} flexWrap="wrap">
                {(selectedRole === "HR" || selectedRole === "Admin") && (
                  <Button
                    variant="outlined"
                    startIcon={<NotificationsIcon />}
                    onClick={handleSendNotifications}
                  >
                    Send Notifications
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenDialog}
                >
                  Assign Compliance Templates
                </Button>
              </Box>
            )}
          </Box>

          {/* ── Row 2: Filters (left) | Export (right) ── */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            mb={2}
          >
            {/* Left: filter controls */}
            <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
              <Autocomplete
                size="small"
                options={projectOptions}
                value={columnFilters.project_name || null}
                onChange={(_, val) => handleFilterChange("project_name", val ?? "")}
                renderInput={(params) => (
                  <TextField {...params} label="Project" placeholder="All" />
                )}
                sx={{ minWidth: 180 }}
              />
              <Autocomplete
                size="small"
                options={templateOptions}
                value={columnFilters.template_name || null}
                onChange={(_, val) => handleFilterChange("template_name", val ?? "")}
                renderInput={(params) => (
                  <TextField {...params} label="Template" placeholder="All" />
                )}
                sx={{ minWidth: 180 }}
              />
              <Autocomplete
                size="small"
                options={assigneeOptions}
                value={columnFilters.assigned_to_name || null}
                onChange={(_, val) => handleFilterChange("assigned_to_name", val ?? "")}
                renderInput={(params) => (
                  <TextField {...params} label="Assigned To" placeholder="All" />
                )}
                sx={{ minWidth: 180 }}
              />
              <FormControl size="small" sx={{ minWidth: 145 }}>
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  label="Status"
                  value={columnFilters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  {COMPLIANCE_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>{titleCase(s)}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Count + Clear — only when a filter is active */}
              {hasActiveFilters && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                    {filteredAssignments.length} / {assignments.length}
                  </Typography>
                  <Button size="small" variant="outlined" color="error" onClick={clearFilters}>
                    Clear
                  </Button>
                </>
              )}
            </Box>

            {/* Right: Export */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={exportToCSV}
              disabled={filteredAssignments.length === 0}
            >
              Export CSV
            </Button>
          </Box>

          {/* ── DataGrid ── */}
          <DataGrid
            rows={filteredAssignments}
            columns={complianceColumns}
            getRowId={(row) => row.assignment_id}
            autoHeight
            {...getDataGridPaginationProps()}
            disableRowSelectionOnClick
            slots={{ noRowsOverlay: CustomNoRowsOverlay }}
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5" },
              "& .MuiDataGrid-cell": { borderBottom: "1px solid #e0e0e0" },
              "& .MuiDataGrid-row:hover": { backgroundColor: "#f0f7ff" },
            }}
          />
        </Paper>
      </Container>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Create Compliance Assignments</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Template, Month & Year, Due Date */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel id="template-select-label">Template</InputLabel>
                <Select
                  labelId="template-select-label"
                  id="template-select"
                  value={formData.template_id}
                  label="Template"
                  onChange={handleTemplateChange}
                >
                  <MenuItem value="">
                    <em>Select a template</em>
                  </MenuItem>
                  {templates.map((template) => (
                    <MenuItem
                      key={template.template_id}
                      value={template.template_id}
                    >
                      {template.template_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Month & Year"
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  views={["month", "year"]}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Due Date"
                  value={customDueDate}
                  onChange={handleDueDateChange}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined",
                      helperText: "Due date is required for compliance assignments",
                      required: true,
                    },
                  }}
                  minDate={today}
                />
              </LocalizationProvider>
            </Grid>

            {/* Assignment Type Selection */}
            <Grid item xs={12}>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Assignment Type
                </Typography>
                <RadioGroup
                  value={assignmentType}
                  onChange={handleAssignmentTypeChange}
                  row
                >
                  <FormControlLabel
                    value="project_based"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">
                          ○ Project-based assignment (default — current behavior)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Internal Project Tracking
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="direct"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">
                          ○ Direct assignment to employee (no project required)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Client-Facing Jira & Non-Developmental Compliance
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Conditional Content Based on Assignment Type */}
            {assignmentType === 'project_based' ? (
              <>
                {/* Project Selection */}
                <Grid item xs={12}>
                  <Autocomplete
                    id="project-select"
                    options={projects}
                    getOptionLabel={(option) => option.project_name}
                    isOptionEqualToValue={(option, value) =>
                      option.project_id === value.project_id
                    }
                    value={selectedProject}
                    onChange={(_, value) => handleProjectChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Project"
                        placeholder="Select a project"
                        variant="outlined"
                      />
                    )}
                  />
                  <FormHelperText>
                    Select a project to assign for compliance
                  </FormHelperText>
                </Grid>

                {previousManager && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 0 }}>
                      Previous assignee: <strong>{previousManager.name}</strong> — pre-selected below.
                    </Alert>
                  </Grid>
                )}

                {/* PCO selection — always shown for project-based assignments */}
                {selectedProject && (
                  <Grid item xs={12}>
                    <Autocomplete
                      id="officer-select"
                      options={complianceOfficers}
                      getOptionLabel={(o) => `${o.name} (${o.email})`}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      value={selectedOfficer}
                      onChange={(_, value) => setSelectedOfficer(value)}
                      loading={loadingOfficers}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Assign to Process Compliance Officer *"
                          placeholder="Search by name or email"
                          variant="outlined"
                          helperText="Select the PCO who will fill this compliance form"
                        />
                      )}
                    />
                  </Grid>
                )}

                {/* Sprint Configuration */}
                {selectedProject && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2, p: 2, border: '1px solid #c8e6c9', borderRadius: 1, bgcolor: '#fafafa' }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Sprint Configuration
                      </Typography>

                      {projectSprints.length === 0 ? (
                        <Alert severity="info">
                          No sprints found for this project. Please add sprints in Jira before
                          creating a compliance assignment.
                        </Alert>
                      ) : (
                        <>
                          <Autocomplete
                            id="sprint-select"
                            options={projectSprints}
                            getOptionLabel={(sprint) => sprint.sprint_name}
                            isOptionEqualToValue={(a, b) => a.sprint_id === b.sprint_id}
                            value={selectedSprints}
                            onChange={(_, value) => setSelectedSprints(value)}
                            filterOptions={(options, { inputValue }) =>
                              options.filter(s =>
                                s.sprint_name.toLowerCase().includes(inputValue.toLowerCase())
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Sprint (Latest Active First)"
                                placeholder={selectedSprints ? "" : "Search sprints…"}
                                variant="outlined"
                              />
                            )}
                          />
                          <FormHelperText>
                            {selectedSprints
                              ? `Selected: ${selectedSprints.sprint_name}`
                              : "Select one sprint for this compliance assignment. Latest active sprint is recommended."}
                          </FormHelperText>
                        </>
                      )}
                    </Box>
                  </Grid>
                )}
              </>
            ) : (
              <>
                {/* Direct Assignment - User Selection */}
                <Grid item xs={12}>
                  {loadingUsers ? (
                    <Box display="flex" alignItems="center" gap={2}>
                      <CircularProgress size={20} />
                      <Typography>Loading employees...</Typography>
                    </Box>
                  ) : (
                    <Autocomplete
                      id="users-select"
                      options={users}
                      getOptionLabel={(option) => `${option.name} (${option.email})`}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      value={selectedUsers}
                      onChange={(_, value) => setSelectedUsers(value)}
                      filterOptions={(options, { inputValue }) =>
                        options.filter(option =>
                          option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                          option.email.toLowerCase().includes(inputValue.toLowerCase())
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Employee"
                          placeholder="Search by name or email"
                          variant="outlined"
                        />
                      )}
                    />
                  )}
                  <FormHelperText>
                    Select one employee to assign compliance directly (no project required)
                  </FormHelperText>
                </Grid>

                {/* Cycle Configuration */}
                <Grid item xs={12}>
                  <Box sx={{ mt: 2, p: 2, border: '1px solid #c8e6c9', borderRadius: 1, bgcolor: '#fafafa' }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Cycle Configuration
                    </Typography>
                    
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Number of Cycles"
                          type="number"
                          value={cycleConfig.cycleCount}
                          onChange={(e) => handleCycleCountChange(parseInt(e.target.value) || 1)}
                          inputProps={{ min: 1 }}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    
                    <Grid container spacing={2}>
                      {cycleConfig.cycleNames.map((cycle, idx) => (
                        <Grid item xs={12} sm={6} md={4} key={idx}>
                          <TextField
                            label={`Cycle ${idx + 1} Name`}
                            value={cycle.name}
                            onChange={(e) => handleCycleNameChange(idx, e.target.value)}
                            placeholder={`Cycle ${idx + 1}`}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Grid>
              </>
            )}

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={isLoading}>
            {isLoading ? "Creating..." : "Assign Compliance Templates"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProcessComplianceAssignments;
